import React, { useContext, useState, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  ClickAwayListener,
  InputBase,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ListItemIcon from '@mui/material/ListItemIcon'
import { firebase_firestore } from '../../../config/firebaseConfig'
import { updateDoc, addDoc, deleteDoc } from 'firebase/firestore'
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore'
import { collection } from 'firebase/firestore'
import { SnackbarHelperContext } from '../../../common/contexts/SnackbarHelperContext'
import {
  FeatureTags,
  FeatureTag,
  FEATURE_TAG_CATEGORIES,
  FEATURE_TAG_LABELS,
  PRODUCT_TAGS,
} from '../../constants/featureTags'
import {
  pageContainer,
  pageHeader,
  pageTitle,
  primaryButton,
  loadingContainer,
  adminColors,
} from '../../adminStyles'

type TagState = 'enabled' | 'disabled' | 'off'

const TAG_STATE_COLORS: Record<TagState, string> = {
  enabled: '#1DC30E',
  disabled: '#f44336',
  off: '#666',
}

const FEATURE_TAG_DESCRIPTIONS: Partial<Record<FeatureTag, string>> = {
  [FeatureTags.CHARTS]: 'Enables access to the interactive charting interface.',
  [FeatureTags.STANDARD_INDICATORS]: 'Unlocks the standard indicator library for use on charts.',
  [FeatureTags.DTU_INDICATORS]:
    'Unlocks DTU proprietary indicators. Automatically assigned for DTU traders.',
  [FeatureTags.CUSTOM_INDICATORS]:
    'Allows building and saving custom composite indicators in Pattern Builder.',
  [FeatureTags.SHOWCASE]: 'Grants access to the Showcase app for viewing and exploring backtests.',
  [FeatureTags.BACKTEST_SETTINGS]:
    'Allows viewing and configuring backtest parameters within Showcase.',
  [FeatureTags.BACKTEST_INSIGHTS]:
    'Enables the backtest insights and analytics dashboard for deeper performance analysis.',
  [FeatureTags.FEATURED_BACKTESTS]:
    'Provides access to curated featured backtests for quicker onboarding.',
  [FeatureTags.PATTERN_BUILDER]:
    'Grants access to the Pattern Builder app for creating and editing trading strategies.',
  [FeatureTags.PRIVATE_PATTERNS]: 'Allows creating and managing private (non-shared) patterns.',
  [FeatureTags.ALERTS]: 'Enables the Alerts app for viewing and managing trading alerts.',
  [FeatureTags.ALERT_CREATE]: 'Allows creating new alerts.',
  [FeatureTags.RESTRICTED_ALERT_TIMEFRAMES]: 'Restricts editing the timeframes on alerts.',
  [FeatureTags.CONDITIONAL_RATIOS]:
    'Enables conditional ratio configuration for backtests and alerts.',
  [FeatureTags.CAN_TOGGLE_TRADING_HOUSE]:
    'Allows the user to switch between GreenChart and DTU trading houses.',
  [FeatureTags.TPA_AFFILIATE]:
    'Assigns the Affiliate Auto Trader product if no current product exists, and creates the Gold Auto Trader alert.',
  [FeatureTags.TRADER_PLUS]:
    'Assigns the Trader Plus product if no current product exists, granting alerts, showcase, and featured backtests access.',
  [FeatureTags.STARTER]: 'Assigns the Starter product if no current product exists.',
  [FeatureTags.STARTER_PLUS]: 'Assigns the Starter Plus product if no current product exists.',
  [FeatureTags.PRO]: 'Assigns the Pro product if no current product exists.',
  [FeatureTags.PRO_PLUS]: 'Assigns the Pro Plus product if no current product exists.',
}

interface ProductFormState {
  name: string
  productTag: string
  alertCount: number
  notifications: number
  recurringCost: number
}

const defaultFormState: ProductFormState = {
  name: '',
  productTag: '',
  alertCount: 0,
  notifications: 0,
  recurringCost: 0,
}

// Numeric fields shown as editable rows in the grid
const NUMERIC_FIELDS: {
  key: string
  label: string
  tooltip: string
  min: number
  max: number
  path: (p: any) => number | undefined
  buildUpdate: (val: number) => any
}[] = [
  {
    key: 'alertCount',
    label: 'Alert Count',
    tooltip: 'Maximum number of alerts this product allows a user to have active simultaneously.',
    min: 0,
    max: 100,
    path: (p) => p.product?.alerts?.alertCount,
    buildUpdate: (val) => ({ 'product.alerts.alertCount': val }),
  },
  {
    key: 'notifications',
    label: 'Notifications',
    tooltip: 'Number of alert notifications included with this product.',
    min: 0,
    max: 100000,
    path: (p) => p.product?.alerts?.notifications,
    buildUpdate: (val) => ({
      'product.alerts.notifications': val,
      'product.alerts.notificationsRemaining': val,
    }),
  },
  {
    key: 'maxAutoTradeAlertsAllowed',
    label: 'Max AutoTraders',
    tooltip:
      'Maximum number of alerts this user can have with autotrading (webhook) enabled simultaneously.',
    min: 0,
    max: 100,
    path: (p) => p.product?.alerts?.maxAutoTradeAlertsAllowed,
    buildUpdate: (val) => ({ 'product.alerts.maxAutoTradeAlertsAllowed': val }),
  },
  {
    key: 'recurringCost',
    label: 'Credits',
    tooltip: 'Recurring credit cost for backtest access.',
    min: 0,
    max: 1000,
    path: (p) => p.product?.backtest?.recurringCost,
    buildUpdate: (val) => ({
      'product.backtest.recurringCost': val,
      'product.backtest.maxCost': val,
    }),
  },
]

function getTagState(tag: FeatureTag, product: any): TagState {
  const tags: string[] = product.tags || []
  const denyTags: string[] = product.denyTags || []
  if (denyTags.includes(tag)) return 'disabled'
  if (tags.includes(tag)) return 'enabled'
  return 'off'
}

export default function ProductTagsEditor() {
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const [products, productsLoading, , productsSnapshot, reloadProducts] = useCollectionDataOnce(
    collection(firebase_firestore, 'products')
  )

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<ProductFormState>(defaultFormState)
  const [saving, setSaving] = useState(false)

  // Tag state menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuTarget, setMenuTarget] = useState<{ productIndex: number; tag: FeatureTag } | null>(
    null
  )

  // Product tag menu
  const [productTagMenuAnchor, setProductTagMenuAnchor] = useState<null | HTMLElement>(null)
  const [productTagMenuTarget, setProductTagMenuTarget] = useState<number | null>(null)

  // Product header menu (rename / delete)
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<null | HTMLElement>(null)
  const [headerMenuProduct, setHeaderMenuProduct] = useState<any | null>(null)

  // Rename
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameName, setRenameName] = useState('')

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Inline edit for numeric fields
  const [editingCell, setEditingCell] = useState<{ productIndex: number; fieldKey: string } | null>(
    null
  )
  const [editValue, setEditValue] = useState('')
  const editingCellRef = useRef<{ productIndex: number; fieldKey: string } | null>(null)
  const editValueRef = useRef('')

  // Updating state
  const [updatingCells, setUpdatingCells] = useState<Set<string>>(new Set())

  const cellKey = (productIndex: number, key: string) => `${productIndex}-${key}`

  const isDuplicateName = useCallback(
    (name: string, excludeId?: string): boolean => {
      const normalized = name.toLowerCase().trim()
      if (!normalized) return false
      return (products || []).some((p: any) => {
        if (excludeId && p.id === excludeId) return false
        return (p.name || '').toLowerCase().trim() === normalized
      })
    },
    [products]
  )

  const handleCreate = async () => {
    const trimmedName = form.name.trim()
    if (!trimmedName) {
      setSnackbarSettings({
        severity: 'error',
        message: 'Product name is required',
        autoHideDuration: 4000,
      })
      return
    }
    if (isDuplicateName(trimmedName)) {
      setSnackbarSettings({
        severity: 'error',
        message: 'A product with this name already exists',
        autoHideDuration: 4000,
      })
      return
    }

    setSaving(true)
    try {
      await addDoc(collection(firebase_firestore, 'products'), {
        name: trimmedName.toLowerCase(),
        productTag: form.productTag || null,
        product: {
          alerts: {
            name: trimmedName.toLowerCase(),
            alertCount: form.alertCount,
            notifications: form.notifications,
            notificationsRemaining: form.notifications,
          },
          backtest: {
            recurringCost: form.recurringCost,
            maxCost: form.recurringCost,
            accruedCost: 0,
          },
        },
        tags: [],
        denyTags: [],
      })
      setSnackbarSettings({
        severity: 'success',
        message: 'Product created',
        autoHideDuration: 3000,
      })
      reloadProducts()
      setCreateOpen(false)
      setForm(defaultFormState)
    } catch (err) {
      console.error('Error creating product:', err)
      setSnackbarSettings({
        severity: 'error',
        message: 'Error creating product',
        autoHideDuration: 6000,
      })
    } finally {
      setSaving(false)
    }
  }

  const getProductRef = (originalIndex: number) => {
    if (!productsSnapshot) return null
    return productsSnapshot.docs[originalIndex]?.ref
  }

  // Tag state change
  const handleTagStateChange = async (
    productIndex: number,
    tag: FeatureTag,
    newState: TagState
  ) => {
    setMenuAnchor(null)
    setMenuTarget(null)

    const product = products?.[productIndex]
    if (!product) return

    const ref = getProductRef(productIndex)
    if (!ref) return

    const key = cellKey(productIndex, tag)
    setUpdatingCells((prev) => new Set(prev).add(key))

    try {
      const currentTags: string[] = product.tags || []
      const currentDenyTags: string[] = product.denyTags || []

      const newTags = currentTags.filter((t) => t !== tag)
      const newDenyTags = currentDenyTags.filter((t) => t !== tag)

      if (newState === 'enabled') {
        newTags.push(tag)
      } else if (newState === 'disabled') {
        newDenyTags.push(tag)
      }

      await updateDoc(ref, { tags: newTags, denyTags: newDenyTags })
      reloadProducts()
    } catch (err) {
      console.error('Error updating tag:', err)
      setSnackbarSettings({
        severity: 'error',
        message: 'Error updating tag',
        autoHideDuration: 6000,
      })
    } finally {
      setUpdatingCells((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  // Numeric field save — reads from refs so it always has the latest values
  const flushNumericSave = async () => {
    const cell = editingCellRef.current
    const val = editValueRef.current
    if (!cell) return

    const { productIndex, fieldKey } = cell
    const field = NUMERIC_FIELDS.find((f) => f.key === fieldKey)
    if (!field) return

    const ref = getProductRef(productIndex)
    if (!ref) return

    const numVal = Number(val)
    if (isNaN(numVal)) {
      editingCellRef.current = null
      setEditingCell(null)
      return
    }
    const clampedVal = Math.max(field.min, Math.min(field.max, Math.floor(numVal)))

    const key = cellKey(productIndex, fieldKey)
    editingCellRef.current = null
    setEditingCell(null)
    setUpdatingCells((prev) => new Set(prev).add(key))

    try {
      await updateDoc(ref, field.buildUpdate(clampedVal))
      reloadProducts()
    } catch (err) {
      console.error('Error updating field:', err)
      setSnackbarSettings({
        severity: 'error',
        message: 'Error updating field',
        autoHideDuration: 6000,
      })
    } finally {
      setUpdatingCells((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const openTagMenu = (
    event: React.MouseEvent<HTMLElement>,
    productIndex: number,
    tag: FeatureTag
  ) => {
    setMenuAnchor(event.currentTarget)
    setMenuTarget({ productIndex, tag })
  }

  const startNumericEdit = (
    productIndex: number,
    fieldKey: string,
    currentValue: number | undefined
  ) => {
    // Flush the previous edit before starting a new one
    if (editingCellRef.current) {
      flushNumericSave()
    }
    const cell = { productIndex, fieldKey }
    const val = String(currentValue ?? 0)
    editingCellRef.current = cell
    editValueRef.current = val
    setEditingCell(cell)
    setEditValue(val)
  }

  const handleProductTagChange = async (originalIndex: number, newTag: string | null) => {
    setProductTagMenuAnchor(null)
    setProductTagMenuTarget(null)

    const ref = getProductRef(originalIndex)
    if (!ref) return

    const key = cellKey(originalIndex, 'productTag')
    setUpdatingCells((prev) => new Set(prev).add(key))

    try {
      await updateDoc(ref, { productTag: newTag })
      reloadProducts()
    } catch (err) {
      console.error('Error updating product tag:', err)
      setSnackbarSettings({
        severity: 'error',
        message: 'Error updating product tag',
        autoHideDuration: 6000,
      })
    } finally {
      setUpdatingCells((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    const ref = getProductRef(deleteTarget._originalIndex)
    if (!ref) return

    setDeleting(true)
    try {
      await deleteDoc(ref)
      setSnackbarSettings({
        severity: 'success',
        message: 'Product deleted',
        autoHideDuration: 3000,
      })
      reloadProducts()
      setDeleteTarget(null)
    } catch (err) {
      console.error('Error deleting product:', err)
      setSnackbarSettings({
        severity: 'error',
        message: 'Error deleting product',
        autoHideDuration: 6000,
      })
    } finally {
      setDeleting(false)
    }
  }

  const handleRename = async () => {
    if (!headerMenuProduct) return
    const trimmed = renameName.trim()
    if (!trimmed) return
    if (isDuplicateName(trimmed)) {
      setSnackbarSettings({
        severity: 'error',
        message: 'A product with this name already exists',
        autoHideDuration: 4000,
      })
      return
    }

    const ref = getProductRef(headerMenuProduct._originalIndex)
    if (!ref) return

    setSaving(true)
    try {
      await updateDoc(ref, {
        name: trimmed.toLowerCase(),
        'product.alerts.name': trimmed.toLowerCase(),
      })
      setSnackbarSettings({
        severity: 'success',
        message: 'Product renamed',
        autoHideDuration: 3000,
      })
      reloadProducts()
      setRenameOpen(false)
      setHeaderMenuProduct(null)
    } catch (err) {
      console.error('Error renaming product:', err)
      setSnackbarSettings({
        severity: 'error',
        message: 'Error renaming product',
        autoHideDuration: 6000,
      })
    } finally {
      setSaving(false)
    }
  }

  if (productsLoading) {
    return (
      <Box sx={loadingContainer}>
        <CircularProgress size={28} sx={{ color: adminColors.accent }} />
      </Box>
    )
  }

  const PRODUCT_NAME_ORDER: string[] = [
    'starter',
    'starter plus',
    'pro',
    'pro plus',
    'affiliate auto trader',
    'trader plus',
    'admin access alerts',
  ]

  const productList = (products || [])
    .map((p: any, i: number) => ({ ...p, _originalIndex: i }))
    .sort((a: any, b: any) => {
      const aName = (a.name || '').toLowerCase().trim()
      const bName = (b.name || '').toLowerCase().trim()
      const aIndex = PRODUCT_NAME_ORDER.indexOf(aName)
      const bIndex = PRODUCT_NAME_ORDER.indexOf(bName)
      const aOrder = aIndex === -1 ? PRODUCT_NAME_ORDER.length : aIndex
      const bOrder = bIndex === -1 ? PRODUCT_NAME_ORDER.length : bIndex
      if (aOrder !== bOrder) return aOrder - bOrder
      return aName.localeCompare(bName)
    })

  return (
    <Box sx={pageContainer}>
      <Box sx={pageHeader}>
        <Typography sx={pageTitle}>Products</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => {
            setForm(defaultFormState)
            setCreateOpen(true)
          }}
          sx={primaryButton}
        >
          Create Product
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          overflow: 'auto',
          border: `1px solid ${adminColors.border}`,
          borderRadius: '8px',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `200px repeat(${productList.length}, minmax(120px, 1fr))`,
            minWidth: 200 + productList.length * 120,
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: `2px solid ${adminColors.border}`,
              position: 'sticky',
              left: 0,
              bgcolor: adminColors.headerBg,
              zIndex: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: adminColors.textSecondary,
              }}
            >
              Feature
            </Typography>
          </Box>
          {productList.map((product: any, pi: number) => {
            const isDeprecated = (product.name || '').toLowerCase().trim() === 'basic'
            return (
              <Box
                key={product.id || pi}
                sx={{
                  p: 1.5,
                  borderBottom: `2px solid ${adminColors.border}`,
                  textAlign: 'center',
                  bgcolor: adminColors.headerBg,
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: adminColors.textPrimary,
                    cursor: 'pointer',
                    '&:hover': { color: adminColors.accent },
                  }}
                  onClick={(e) => {
                    setHeaderMenuAnchor(e.currentTarget)
                    setHeaderMenuProduct(product)
                  }}
                >
                  {product.name || product.id}
                </Typography>
                {isDeprecated && (
                  <Chip
                    label="Deprecated"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }}
                  />
                )}
              </Box>
            )
          })}

          {/* Product config rows */}
          <Box
            sx={{
              gridColumn: '1 / -1',
              p: 1,
              px: 1.5,
              bgcolor: adminColors.headerBg,
              borderBottom: `1px solid ${adminColors.borderLight}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: adminColors.textMuted,
              }}
            >
              Product Configuration
            </Typography>
          </Box>

          {/* Product Tag row */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: `1px solid ${adminColors.borderLight}`,
              display: 'flex',
              alignItems: 'center',
              position: 'sticky',
              left: 0,
              bgcolor: adminColors.cardBg,
              zIndex: 1,
            }}
          >
            <Tooltip
              title="Product identifier tag used to assign this product to users."
              arrow
              placement="right"
            >
              <Typography
                variant="body2"
                sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.secondary' }}
              >
                Product Tag
              </Typography>
            </Tooltip>
          </Box>
          {productList.map((product: any) => {
            const oi = product._originalIndex
            const key = cellKey(oi, 'productTag')
            const isUpdating = updatingCells.has(key)

            return (
              <Box
                key={key}
                sx={{
                  p: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isUpdating ? 'default' : 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={(e) => {
                  if (isUpdating) return
                  setProductTagMenuAnchor(e.currentTarget)
                  setProductTagMenuTarget(oi)
                }}
              >
                {isUpdating ? (
                  <CircularProgress size={18} />
                ) : product.productTag ? (
                  <Chip
                    label={
                      FEATURE_TAG_LABELS[product.productTag as FeatureTag] || product.productTag
                    }
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    None
                  </Typography>
                )}
              </Box>
            )
          })}

          {NUMERIC_FIELDS.map((field) => (
            <React.Fragment key={field.key}>
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'sticky',
                  left: 0,
                  bgcolor: adminColors.cardBg,
                  zIndex: 1,
                }}
              >
                <Tooltip title={field.tooltip} arrow placement="right">
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: 'help',
                      borderBottom: '1px dotted',
                      borderColor: 'text.secondary',
                    }}
                  >
                    {field.label}
                  </Typography>
                </Tooltip>
              </Box>
              {productList.map((product: any) => {
                const oi = product._originalIndex
                const key = cellKey(oi, field.key)
                const isUpdating = updatingCells.has(key)
                const isEditing =
                  editingCell?.productIndex === oi && editingCell?.fieldKey === field.key
                const value = field.path(product)

                return (
                  <Box
                    key={key}
                    sx={{
                      p: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isUpdating ? 'default' : 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() =>
                      !isUpdating && !isEditing && startNumericEdit(oi, field.key, value)
                    }
                  >
                    {isUpdating ? (
                      <CircularProgress size={18} />
                    ) : isEditing ? (
                      <ClickAwayListener onClickAway={flushNumericSave}>
                        <InputBase
                          value={editValue}
                          onChange={(e) => {
                            setEditValue(e.target.value)
                            editValueRef.current = e.target.value
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') flushNumericSave()
                            if (e.key === 'Escape') {
                              editingCellRef.current = null
                              setEditingCell(null)
                            }
                          }}
                          autoFocus
                          type="number"
                          inputProps={{ min: field.min, max: field.max }}
                          sx={{
                            width: 80,
                            textAlign: 'center',
                            '& input': { textAlign: 'center', p: 0.5 },
                            border: '1px solid',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                          }}
                        />
                      </ClickAwayListener>
                    ) : (
                      <Typography variant="body2" fontWeight={500}>
                        {value ?? '-'}
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </React.Fragment>
          ))}

          {/* Feature tag category sections */}
          {FEATURE_TAG_CATEGORIES.map((category) => (
            <React.Fragment key={category.name}>
              {/* Category header */}
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  p: 1,
                  bgcolor: 'action.hover',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: adminColors.textMuted,
                  }}
                >
                  {category.name}
                </Typography>
              </Box>

              {/* Tag rows */}
              {category.tags.map((tag) => (
                <React.Fragment key={tag}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'sticky',
                      left: 0,
                      bgcolor: adminColors.cardBg,
                      zIndex: 1,
                    }}
                  >
                    <Tooltip title={FEATURE_TAG_DESCRIPTIONS[tag] || ''} arrow placement="right">
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: 'help',
                          borderBottom: FEATURE_TAG_DESCRIPTIONS[tag] ? '1px dotted' : 'none',
                          borderColor: 'text.secondary',
                        }}
                      >
                        {FEATURE_TAG_LABELS[tag] || tag}
                      </Typography>
                    </Tooltip>
                  </Box>
                  {productList.map((product: any) => {
                    const oi = product._originalIndex
                    const state = getTagState(tag, product)
                    const key = cellKey(oi, tag)
                    const isUpdating = updatingCells.has(key)

                    return (
                      <Box
                        key={key}
                        sx={{
                          p: 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isUpdating ? 'default' : 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={(e) => !isUpdating && openTagMenu(e, oi, tag)}
                      >
                        {isUpdating ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              color: TAG_STATE_COLORS[state],
                              textTransform: 'uppercase',
                              fontSize: '0.75rem',
                              letterSpacing: 0.5,
                            }}
                          >
                            {state}
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </Box>
      </Paper>

      {/* Product header menu */}
      <Menu
        anchorEl={headerMenuAnchor}
        open={Boolean(headerMenuAnchor)}
        onClose={() => {
          setHeaderMenuAnchor(null)
          setHeaderMenuProduct(null)
        }}
      >
        <MenuItem
          onClick={() => {
            setHeaderMenuAnchor(null)
            setRenameName(headerMenuProduct?.name || '')
            setRenameOpen(true)
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setHeaderMenuAnchor(null)
            setDeleteTarget(headerMenuProduct)
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Tag state menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null)
          setMenuTarget(null)
        }}
      >
        {(['enabled', 'disabled', 'off'] as TagState[]).map((state) => (
          <MenuItem
            key={state}
            onClick={() =>
              menuTarget && handleTagStateChange(menuTarget.productIndex, menuTarget.tag, state)
            }
            sx={{ minWidth: 120 }}
          >
            <Typography
              sx={{ color: TAG_STATE_COLORS[state], fontWeight: 500, textTransform: 'capitalize' }}
            >
              {state}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Product tag menu */}
      <Menu
        anchorEl={productTagMenuAnchor}
        open={Boolean(productTagMenuAnchor)}
        onClose={() => {
          setProductTagMenuAnchor(null)
          setProductTagMenuTarget(null)
        }}
      >
        <MenuItem
          onClick={() =>
            productTagMenuTarget != null && handleProductTagChange(productTagMenuTarget, null)
          }
        >
          <Typography color="text.secondary">None</Typography>
        </MenuItem>
        {PRODUCT_TAGS.map((tag) => (
          <MenuItem
            key={tag}
            onClick={() =>
              productTagMenuTarget != null && handleProductTagChange(productTagMenuTarget, tag)
            }
          >
            <Typography>{FEATURE_TAG_LABELS[tag] || tag}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Create Product Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Product Name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
              error={isDuplicateName(form.name)}
              helperText={
                isDuplicateName(form.name) ? 'A product with this name already exists' : ''
              }
            />
            <FormControl fullWidth>
              <InputLabel>Product Tag</InputLabel>
              <Select
                value={form.productTag}
                onChange={(e) => setForm((prev) => ({ ...prev, productTag: e.target.value }))}
                label="Product Tag"
              >
                <MenuItem value="">None</MenuItem>
                {PRODUCT_TAGS.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {FEATURE_TAG_LABELS[tag] || tag}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Alerts Configuration
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Alert Count"
                type="number"
                value={form.alertCount}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    alertCount: Math.max(0, Math.min(100, Number(e.target.value))),
                  }))
                }
                inputProps={{ min: 0, max: 100 }}
                fullWidth
              />
              <TextField
                label="Notifications"
                type="number"
                value={form.notifications}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    notifications: Math.max(0, Math.min(100000, Number(e.target.value))),
                  }))
                }
                inputProps={{ min: 0, max: 100000 }}
                fullWidth
              />
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Backtest Configuration
            </Typography>
            <TextField
              label="Credits"
              type="number"
              value={form.recurringCost}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  recurringCost: Math.max(0, Math.min(1000, Number(e.target.value))),
                }))
              }
              inputProps={{ min: 0, max: 1000 }}
              fullWidth
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Feature tags can be configured after the product is created.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={saving || !form.name.trim() || isDuplicateName(form.name)}
          >
            {saving ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameOpen}
        onClose={() => !saving && setRenameOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Product</DialogTitle>
        <DialogContent>
          <TextField
            label="Product Name"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            required
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            error={isDuplicateName(renameName)}
            helperText={
              isDuplicateName(renameName) ? 'A product with this name already exists' : ''
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameName.trim() && !isDuplicateName(renameName))
                handleRename()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            variant="contained"
            disabled={saving || !renameName.trim() || isDuplicateName(renameName)}
          >
            {saving ? <CircularProgress size={24} /> : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Delete Product</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
              </Typography>

              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: 'error.dark', borderColor: 'error.main' }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'error.contrastText' }}>
                  This action will:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'error.contrastText' }}>
                  <li>
                    <Typography variant="body2">
                      Permanently remove this product definition from Firestore
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Users currently assigned to this product will no longer resolve feature tags
                      from it
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Any feature tags ({(deleteTarget.tags || []).length} enabled,{' '}
                      {(deleteTarget.denyTags || []).length} denied) configured on this product will
                      stop applying
                    </Typography>
                  </li>
                  {deleteTarget.productTag && (
                    <li>
                      <Typography variant="body2">
                        The product tag{' '}
                        <strong>
                          {FEATURE_TAG_LABELS[deleteTarget.productTag as FeatureTag] ||
                            deleteTarget.productTag}
                        </strong>{' '}
                        will no longer be associated with any product
                      </Typography>
                    </li>
                  )}
                </Box>
              </Paper>

              <Typography variant="body2" color="text.secondary">
                This cannot be undone. Existing users will need to be reassigned to a different
                product.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete Product'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
